/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { actions } from '@/redux/av-check';
import ApiService from '@/services';
import {
  AddressType, AvailabilityCheckMetaType,
  AvailabilityCheckPageValueType,
  AvCheckFormData, AvCheckRequestBody,
  CmsPageContentIdentifier,
  CmsPageContentType, Feature,
  ReduxStateType,
  ResultType,
  TargetGroup,
  TariffOfferType,
} from '@/types/av-check';

import AvailabilityChecker from '@/components/av-check/AvailabilityChecker';
import AlertBox from '@/components/av-check/AlertBox';
import OfferRows from '@/components/av-check/OfferRows';
import { Alert } from '@/components/compendium';
import AlertType from '@/components/compendium/Alert/AlertType';
import useMaxBandwidthInformation from '@/components/av-check/PositiveResult/use-max-bandwidth-information';
import {
  getOffersForContractDuration, getOffersForTargetGroup, interpolate,
} from '@/utils';
import {
  buildCheckOtherAddressDataPopup,
  buildErrorDataPopup,
  buildSuccessDataPopup,
  buildWarningDataPopup,
  pushToTefDataLayer,
} from '@/services/tracking';
import DtagSpecificationView from '@/components/av-check/DtagSpecificationOverlay';


export interface AvCheckWebComponentProps {
  serviceUrl: string,
  offerName: string,
  shopContext: string,
  targetGroup: TargetGroup,
  contractDuration: number,
  dispatchEvent: Function,
  selectedPack?: string,
  selectedRouter?: string,
  hardwareOfferName?: string,
  manufacturerHeroOffer?: string;
  hardwareGroupNameHeroOffer?: string;
  byshColor?: string;
  byshStorageSize?: number;
  byshVertragsart?: string;
  byshRatenzahlung?: number;
  tariffVariationCode?: string;
  accessType?: string;
}

export interface StateProps {
  cmsContent: CmsPageContentType,
  address: AddressType,
  result?: ResultType,
  resultError: boolean,
  tariffOffers: TariffOfferType[],
  enabledFeatures: Feature[],
  meta: AvailabilityCheckMetaType,
}

export interface DispatchProps {
  setAddress: Function,
  setPageValue: Function,
  setResult: Function,
  setResultError: Function,
  setCurrentTargetGroup: Function,
  setCurrentContractDuration: Function,
}

const AvCheckWebComponent = ({
  setAddress,
  setPageValue,
  address,
  setResult,
  setCurrentTargetGroup,
  setCurrentContractDuration,
  setResultError,
  result,
  serviceUrl,
  offerName,
  shopContext,
  cmsContent,
  tariffOffers,
  enabledFeatures,
  meta,
  targetGroup,
  contractDuration,
  dispatchEvent,
  selectedPack,
  selectedRouter,
  hardwareOfferName,
  manufacturerHeroOffer,
  hardwareGroupNameHeroOffer,
  byshColor,
  byshStorageSize,
  byshVertragsart,
  byshRatenzahlung,
  tariffVariationCode,
  accessType
}: AvCheckWebComponentProps & StateProps & DispatchProps) => {
  const apiService = new ApiService(serviceUrl);
  const [requestPending, setRequestPending] = useState(false);
  const [showDTAGSpecificationView, setShowDTAGSpecificationView] = useState(false);
  const [dtagSpecificAvChecked, setDtagSpecificAvChecked] = useState(false);
  const [showPdpActivationCostInfo, setShowPdpActivationCostInfo] = useState(false);
  const [showPdpDevelopmentCostInfo, setShowPdpDevelopmentCostInfo] = useState(false);
  const [selectedOfferName, setSelectedOfferName] = useState<string>(offerName);

  useEffect(
    () => {
      apiService.getPageValue()
        .then((pageValue) => {
          setPageValue(pageValue);
        });

      apiService.getStoredResult(shopContext)
        .then((avCheckResult) => {
          if (avCheckResult) {
            setResult(avCheckResult);
            setCurrentTargetGroup(targetGroup);
            setCurrentContractDuration(contractDuration);
            setAddress(avCheckResult.address);
          }
        })
        .catch((err) => {
          console.error('Unable to get stored result:', err);
        });
    },
    [],
  );

  const onAvailabilityCheck = (checkFormData: AvCheckFormData) => {
    setRequestPending(true);
    setResult(undefined);
    setResultError(false);
    let avCheckPending = true;
    let avCheckResult: ResultType;
    let resultError = false;

    const startTimeout = (timeout: number) => {
      setTimeout(() => {
        if (!avCheckPending) {
          if (resultError) {
            if (dtagSpecificAvChecked) {
              //ASRI does not explain the expected bahavior if DTAG specific
              //second avcheck call fails. So we simply reset the av-check form
              //for the time being to let user try again.
              setDtagSpecificAvChecked(false);
              setResult(undefined);
              setResultError(false);
              setAddress({
                zipCode: '',
                city: '',
                street: '',
                houseNumber: '',
              });
            } else {
              setResultError(resultError);
            }
          } else {
            setResult(avCheckResult);
            if (avCheckResult.address) {
              setAddress(avCheckResult.address);
            }
          }
          setShowDTAGSpecificationView(false);
          setRequestPending(false);
        } else {
          // in the case that av check takes longer than 3s
          // timeout will be increased by 1s until the av check is completed
          startTimeout(1000);
        }
      }, timeout);
    };

    // 3s as initial timeout is needed to make sure the loading modal will be shown
    // at least 3s whenever the av result is returned
    startTimeout(3000);

    const heroOfferData = manufacturerHeroOffer ?
      {
        manufacturerHeroOffer,
        hardwareGroupNameHeroOffer,
        byshColor,
        byshStorageSize,
        byshVertragsart,
        byshRatenzahlung,
      } : undefined;

    const newAVCheckFormData: AvCheckFormData = {
      ...checkFormData,
      tariffVariationCode: tariffVariationCode,
      accessType: accessType
    }

    apiService.checkAvailability(new AvCheckRequestBody(newAVCheckFormData, heroOfferData), shopContext, selectedPack, selectedRouter, hardwareOfferName)
      .then((resultResponse) => {
        avCheckPending = false;
        avCheckResult = resultResponse as any;
        setCurrentTargetGroup(targetGroup);
        setCurrentContractDuration(contractDuration);
        if (avCheckResult.address) {
          const resultAddress = `${avCheckResult.address.zipCode} ${avCheckResult.address.city}`;
          if (avCheckResult.tariffOffers.length === 0
              || !avCheckResult.tariffOffers.map((o: { offerName: string; }) => o.offerName).includes( selectedOfferName)) {
            pushToTefDataLayer(window, buildWarningDataPopup('no offers found!', selectedOfferName, resultAddress));
          } else {
            pushToTefDataLayer(window, buildSuccessDataPopup(selectedOfferName, resultAddress));
          }
        } else {
          pushToTefDataLayer(window, buildWarningDataPopup('no address found!', selectedOfferName, `${checkFormData.zipCode} ${checkFormData.city}`));
        }
      })
      .catch((err) => {
        avCheckPending = false;
        resultError = true;
        console.error('Unable to check availability:', err);
        pushToTefDataLayer(window, buildErrorDataPopup(err, `${checkFormData.zipCode} ${checkFormData.city}`));
      });
  };

  const onCheckAnotherAddress = () => {
    !showDTAGSpecificationView && pushToTefDataLayer(window, buildCheckOtherAddressDataPopup(resultAddress, selectedOfferName));
    setResult(undefined);
    setResultError(false);
    setAddress({
      zipCode: '',
      city: '',
      street: '',
      houseNumber: '',
    });
  };

  const bandwidthInformation = useMaxBandwidthInformation(cmsContent, result?.maxBandwidthForAccessTechnology);

  const offersForTargetGroup = getOffersForTargetGroup(tariffOffers, targetGroup)
    .sort((a, b) => b.technicalRank - a.technicalRank);

  if (result === undefined || offersForTargetGroup.length === 0) {
    return (
      <>
        {result !== undefined && offersForTargetGroup.length === 0 && tariffOffers.length !== 0
          && (
            <AlertBox
              type="warning"
              contentIdentifier={CmsPageContentIdentifier.MessageSelectedNotAvailable}
              contentParams={{ ...address }}
            />
          )}
        <AvailabilityChecker
          isPdpCall={true}
          withPendingStyle
          avCheckPending={requestPending}
          onAvailabilityCheck={onAvailabilityCheck}
          onCityListFetch={(zipCode: string, onSuccess: (cities: string[]) => void) => (
            apiService.getCitiesByZipcode(zipCode, onSuccess)
          )}
          onStreetListFetch={
            (zipCode: string, city: string, onSuccess: (streets: string[]) => void) => (
              apiService.getStreetsByCity(zipCode, city, onSuccess)
            )
          }
          displayHouseholdConfigurator={false}
          showDTAGSpecificationView={showDTAGSpecificationView}
        />
      </>
    );
  }

  const selectedOffer = tariffOffers.filter((o) => o.offerName === selectedOfferName)[0];
  const originalOffer = tariffOffers.filter((o) => o.offerName === offerName)[0];

  const offersForTargetGroupAndContractDuration = getOffersForContractDuration(offersForTargetGroup, contractDuration);

  const selectedOfferIndex = offersForTargetGroupAndContractDuration.indexOf(selectedOffer);

  const nextOfferByTechnicalRankWithType = () => offersForTargetGroupAndContractDuration
    .slice(0, selectedOfferIndex)
    .reverse()
    .find((offer) => offer.technicalRank !== selectedOffer.technicalRank
      && offer.type === selectedOffer.type
      && offer.simOnly === selectedOffer.simOnly);

  const isBundleOffer = () => hardwareOfferName !== undefined && hardwareOfferName !== '';

  const upsellOffer = !isBundleOffer() && selectedOffer && selectedOfferIndex > 0
    ? nextOfferByTechnicalRankWithType() : undefined;

  const alternativeOffer = selectedOffer
    ? upsellOffer : offersForTargetGroupAndContractDuration[0] || offersForTargetGroup[0];

  const alternativeOfferHint = selectedOffer
    ? CmsPageContentIdentifier.UpSellHint : CmsPageContentIdentifier.AlternativeHint;

  const alertMessage = selectedOffer
    ? CmsPageContentIdentifier.MessageSelectedAvailable
    : CmsPageContentIdentifier.MessageSelectedNotAvailable;

  const alertContent = interpolate(cmsContent[alertMessage], { ...address });

  const resultAddress = `${address.zipCode} ${address.city}`;

  const technologyPricingLabels = {
    DISCOUNT: cmsContent[CmsPageContentIdentifier.TechnologyPricingDiscountLabel],
    SURCHARGE: cmsContent[CmsPageContentIdentifier.TechnologyPricingSurchargeLabel],
    MONTHLYLIST: cmsContent[CmsPageContentIdentifier.TechnologyPricingMonthlyList],

  };

  const backToDtagSpecificationViewHandler = () => {
    setShowDTAGSpecificationView(true);
  };

  if ( showDTAGSpecificationView) {
    return (
        <>
          <DtagSpecificationView
              onAvailabilityCheck={onAvailabilityCheck}
              setShowDTAGSpecificationView={setShowDTAGSpecificationView}
              setDtagSpecificAvChecked={setDtagSpecificAvChecked}
              onCheckAnotherAddress={onCheckAnotherAddress}
              isDtagGuiEnhancementsToggleEnabledForShop = {(!meta.cciTheme && enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS))}
              isDtagGuiEnhancementsToggleEnabledForCci = {(meta.cciTheme && enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS_CCI))}
          />
        </>
    );
  }

  const hidePdpActivationCostInfo = () => {
    setShowPdpActivationCostInfo(false);
  };

  const hidePdpDevelopmentCostInfo = () => {
    setShowPdpDevelopmentCostInfo(false);
  };

  if (showPdpActivationCostInfo) {
    return (
        <div className="modal-availability-check">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <p dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.ActivationCostInfoDescription] }} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={hidePdpActivationCostInfo}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
    );
  }

  if (showPdpDevelopmentCostInfo) {
    return (
        <div className="modal-availability-check">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <p dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.DevelopmentCostInfoDescription] }} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={hidePdpDevelopmentCostInfo}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
    );
  }

  const isDtagFtthToggleEnabled = (!meta.cciTheme && enabledFeatures.includes(Feature.DTAG_FTTH_WEBSHOP)) ||
      (meta.cciTheme && enabledFeatures.includes(Feature.DTAG_FTTH_CCI));
  const showBackToHtsLink = !selectedOffer;
  return (
      <div className="modal-availability-check">
        <div className="modal-dialog modal-lg modal-has-header modal-has-footer">
          <div className="modal-content">
            <div className="modal-header">
              <Alert type={selectedOffer ? AlertType.SUCCESS : AlertType.WARNING} noBorder>
                <div>
                  <div dangerouslySetInnerHTML={{ __html: alertContent }} />
                  { selectedOffer && <div dangerouslySetInnerHTML={{ __html: bandwidthInformation.content }} />}
                </div>

              </Alert>
              {result && result.address && result.address.corrected
                  && <AlertBox type="info" contentIdentifier={CmsPageContentIdentifier.InfoMessageCorrectedAddress} />}
            </div>
            <div className="modal-body">
              <OfferRows
                  offer={selectedOffer}
                  alternativeOffer={alternativeOffer}
                  alternativeOfferHint={alternativeOfferHint}
                  dispatchEvent={dispatchEvent}
                  technologyPricingLabels={technologyPricingLabels}
                  setShowDTAGSpecificationView={setShowDTAGSpecificationView}
                  setSelectedOfferName={setSelectedOfferName}
                  dtagSpecificAvChecked={dtagSpecificAvChecked}
                  setDtagSpecificAvChecked={setDtagSpecificAvChecked}
                  setShowPdpDevelopmentCostInfo={setShowPdpDevelopmentCostInfo}
                  setShowPdpActivationCostInfo={setShowPdpActivationCostInfo}
                  originalOffer={originalOffer}
                  originalOfferName={offerName}
              />
            </div>
            <div className="modal-footer" style={{ position: 'relative' }}>
              <p className="footer-content">
                <a
                    className="link-icon-left link-bold"
                    onClick={onCheckAnotherAddress}
                >
                  <span className="glyphicon glyphicon-arrow-left" />
                  <span dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.CheckOtherAddressLink] }} />
                </a>

              </p>
            </div>
            { isDtagFtthToggleEnabled && showBackToHtsLink &&  <div>
              <br/>
              <a className="link-icon-right link-bold"
                 href='internet-festnetz'>
                <span style={{ float: 'right' }}>Zu den Tarifen {'>'}</span>
              </a>
            </div>}
          </div>
        </div>
      </div>
  );
};

const mapStateToProps = (state: ReduxStateType) => ({
  cmsContent: state.cmsContent,
  address: state.address,
  result: state.result,
  resultError: state.resultError,
  tariffOffers: state.result?.tariffOffers || [],
  enabledFeatures: state.enabledFeatures,
  meta: state.meta,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setAddress: (address: AddressType) => dispatch(actions.setAddress(address)),
  setPageValue: (pageValue: AvailabilityCheckPageValueType) => dispatch(actions.setPageValue(pageValue)),
  setResult: (result: ResultType) => dispatch(actions.setResult(result)),
  setCurrentTargetGroup: (targetGroup: TargetGroup) => dispatch(actions.setCurrentTargetGroup(targetGroup)),
  setCurrentContractDuration: (contractDuration: number) => dispatch(actions.setCurrentContractDuration(contractDuration)),
  setResultError: (isError: boolean) => dispatch(actions.setResultError(isError)),
});

export default connect<StateProps, DispatchProps, AvCheckWebComponentProps, ReduxStateType>(
  mapStateToProps,
  mapDispatchToProps,
)(AvCheckWebComponent);
